/**
 * Whop Plan Service - Source of truth routing based on plan ID
 * Checks Whop directly to determine product (UGC vs Coaching) and routes accordingly
 */

const WHOP_API_KEY = import.meta.env?.VITE_WHOP_API_KEY;
// Comma-separated admin emails that should bypass Whop checks
const ADMIN_EMAIL_ALLOWLIST = (import.meta.env?.VITE_ADMIN_EMAIL_ALLOWLIST || '')
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

const isAdminEmail = (email) => {
  if (!email) return false;
  const normalized = String(email).trim().toLowerCase();
  return ADMIN_EMAIL_ALLOWLIST.includes(normalized);
};

const coachingPlanIds = new Set([
  'plan_DBXVLNLTqBIYK',
  'plan_2nQAUoWggHLBk',
  'plan_eXbZqIxnR13uz',
  'plan_auyU5uFXjcyIE',
]);

const ugcPlanIds = new Set([
  'plan_5BLztZ9jC4KzK',
  'plan_noxLXIJbAgqD3',
  'plan_0BaO8IscYTvfS',
  'plan_Q9XYlNHreWOJU',
  'plan_oCcNRWEpF7Go8',
]);

/**
 * Get user's active membership from Whop by email
 * Returns the plan ID and membership details
 */
export const getWhopMembershipByEmail = async (email) => {
  if (!email || !WHOP_API_KEY) {
    return null;
  }

  try {
    // Query Whop API for user memberships
    const response = await fetch(
      `https://api.whop.com/api/v1/memberships?email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${WHOP_API_KEY}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      console.warn(`Whop API returned ${response.status} for email ${email}`);
      // Treat non-2xx as "no active membership" but do not grant access downstream
      return null;
    }

    const data = await response.json();
    const memberships = data?.data || [];

    // Find first active membership
    const activeMembership = memberships.find(
      (m) => m.valid === true && m.plan?.id
    );

    if (!activeMembership) {
      return null;
    }

    return {
      planId: activeMembership.plan.id,
      planName: activeMembership.plan.name,
      membershipId: activeMembership.id,
      valid: activeMembership.valid,
      product: getProductFromPlanId(activeMembership.plan.id),
    };
  } catch (error) {
    console.error('Error fetching Whop membership:', error);
    return null;
  }
};

/**
 * Determine product type from plan ID
 */
export const getProductFromPlanId = (planId) => {
  if (!planId) return null;
  if (coachingPlanIds.has(planId)) return 'coaching';
  if (ugcPlanIds.has(planId)) return 'ugc';
  
  // Fallback: if it's an unknown plan, assume UGC to avoid locking paid users
  return 'ugc';
};

/**
 * Get routing destination based on product
 */
export const getOnboardingPath = (product) => {
  if (product === 'ugc') {
    return '/app/ugc-onboarding';
  }
  return '/app/industry-selection'; // coaching
};

/**
 * Determine redirect after auth based on Whop plan
 */
export const getPostAuthRedirect = async (email, userProfile) => {
  // Admin bypass: allow listed emails skip Whop gating and go straight to dashboard
  if (isAdminEmail(email)) {
    return '/app/dashboard';
  }

  // First, check Whop for active plan
  const whopMembership = await getWhopMembershipByEmail(email);
  
  if (whopMembership) {
    // User has active Whop subscription - route by product
    const product = whopMembership.product;
    const onboardingPath = getOnboardingPath(product);
    
    // If they're already done onboarding for this product, go to dashboard
    if (product === 'ugc' && userProfile?.ugc_onboarding_completed) {
      return '/app/ugc-dashboard';
    }
    if (product === 'coaching' && userProfile?.onboarding_completed) {
      return '/app/dashboard';
    }
    
    // Otherwise go to onboarding
    return onboardingPath;
  }
  
  // No active Whop plan
  // If Whop is configured (API key present) and no membership found, keep user out of coaching
  // to avoid granting access on Whop failures/null responses.
  if (WHOP_API_KEY) {
    return '/pre-checkout';
  }

  // Whop not configured: fall back to database state
  if (userProfile?.onboarding_completed && userProfile?.industry) {
    // Has coaching onboarding, go to coaching
    return '/app/dashboard';
  }
  
  // Default to coaching industry selection
  return '/app/industry-selection';
};

export default {
  getWhopMembershipByEmail,
  getProductFromPlanId,
  getOnboardingPath,
  getPostAuthRedirect,
};
